import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import InvoiceUploader from "@/components/InvoiceUploader";
import { RequestDetailsLoader } from "@/components/RequestDetailsPanel";

export const PAY_MODES = ["NEFT", "RTGS", "IMPS", "UPI", "Cheque", "Cash"];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export function useRkfBanks() {
  const [banks, setBanks] = useState([]);
  useEffect(() => { api.get("/masters/rkf-bank-accounts").then(r => setBanks((r.data || []).filter(b => b.active))).catch(() => setBanks([])); }, []);
  return banks;
}

export function PaymentFields({ f, setF, banks, selfPay, testId = "pay", optional = false }) {
  const utrRequired = selfPay || f.mode !== "Cash";
  const req = optional ? "" : " *";
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><Label>Payment date *</Label><Input type="date" required value={f.pay_date} onChange={e => setF({ ...f, pay_date: e.target.value })} data-testid={`${testId}-date`} /></div>
        <div><Label>Mode *</Label>
          <Select value={f.mode} onValueChange={v => setF({ ...f, mode: v })}>
            <SelectTrigger data-testid={`${testId}-mode`}><SelectValue /></SelectTrigger>
            <SelectContent>{PAY_MODES.filter(m => !(selfPay && m === "Cash")).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>UTR / cheque / ref no. {utrRequired ? "*" : "(optional for Cash)"}</Label><Input value={f.utr} onChange={e => setF({ ...f, utr: e.target.value })} data-testid={`${testId}-utr`} /></div>
      </div>
      <div><Label>Paid from (RKF bank account){req}</Label>
        <Select value={f.paid_from_id} onValueChange={v => setF({ ...f, paid_from_id: v })}>
          <SelectTrigger data-testid={`${testId}-paid-from`}><SelectValue placeholder={banks.length ? "Pick account" : "No RKF bank accounts in Masters yet"} /></SelectTrigger>
          <SelectContent>{banks.map(b => <SelectItem key={b.id} value={b.id}>{b.display}</SelectItem>)}</SelectContent>
        </Select>
        {!banks.length && <p className="text-[11px] text-amber-700 mt-1">Super Admin adds RKF bank accounts in Masters › RKF bank accounts.</p>}
      </div>
      <InvoiceUploader label={`Payment proof${req} (bank screenshot / cheque copy)`} files={f.proof} onChange={v => setF({ ...f, proof: v.slice(-1) })} testId={`${testId}-proof`} />
    </div>
  );
}

const blank = () => ({ pay_date: new Date().toISOString().slice(0, 10), mode: "NEFT", utr: "", paid_from_id: "", proof: [], note: "" });
export const paymentMissing = (f, selfPay) => !f.paid_from_id ? "Pick the RKF bank account" : !f.proof.length ? "Attach the payment proof"
  : (selfPay || f.mode !== "Cash") && !f.utr.trim() ? "UTR / reference is required" : null;

function PayeeBank({ payee, selfPay }) {
  if (!payee) return null;
  return (
    <div className="rounded border bg-slate-50 p-3 text-sm" data-testid="disburse-payee-bank">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Pay to (from Masters · read-only)</div>
      <div className="font-medium">{payee.name}</div>
      <div className="text-xs text-slate-600 font-mono">{payee.bank_name || "—"} · {payee.account_masked || "no account"} · IFSC {payee.ifsc || "—"}</div>
      {!payee.bank_complete && <div className="text-xs text-rose-700 mt-1" data-testid="disburse-bank-incomplete">Bank details incomplete in Masters{selfPay ? " – your own advance cannot be paid until they are complete." : "."}</div>}
      {selfPay && <div className="text-xs text-amber-800 mt-1">Your own advance: paid only to your registered account above. It will be marked "Self-disbursed (Accounts)" and the Super Admin is notified.</div>}
    </div>
  );
}

export function DisburseForm({ adv, onSaved }) {
  const [ctx, setCtx] = useState(null);
  const [f, setF] = useState(blank());
  const [amount, setAmount] = useState(adv.approved_amount || adv.amount);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/advances/${adv.id}/disburse-context`).then(r => setCtx(r.data)).catch(() => setCtx({ banks: [] })); }, [adv.id]);
  const approved = Number(ctx?.approved_amount || adv.amount || 0);
  const amt = Number(amount || 0);
  const selfPay = !!ctx?.self;
  const block = !ctx ? "Loading…" : selfPay && !ctx.final_by_super_admin ? "Your own advance needs Final approval by the Super Admin first"
    : selfPay && !ctx.payee?.bank_complete ? "Your registered bank account is incomplete" : amt <= 0 ? "Enter the amount"
    : amt > approved + 0.01 ? `Amount cannot exceed the approved ${inr(approved)}` : paymentMissing(f, selfPay);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/advances/${adv.id}/disburse`, { pay_date: f.pay_date, mode: f.mode, utr: f.utr, paid_from_id: f.paid_from_id, amount: amt, proof_file_id: f.proof[0]?.id, note: f.note });
      toast.success(selfPay ? "Disbursed – marked Self-disbursed, Super Admin notified" : "Disbursed");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>Disburse · {adv.advance_id}</DialogTitle></DialogHeader>
      <div className="space-y-3 mt-2" data-testid="disburse-form">
        <PayeeBank payee={ctx?.payee} selfPay={selfPay} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Amount * <span className="text-slate-400 font-normal">(approved {inr(approved)})</span></Label>
            <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} data-testid="disburse-amount" /></div>
          <div className="text-xs self-end pb-2" data-testid="disburse-balance">
            {amt > approved + 0.01 ? <span className="text-rose-700 font-semibold">More than approved – not allowed</span>
              : approved - amt > 0.01 ? <span className="text-amber-800">{inr(approved - amt)} will not be disbursed – it is released back to the budget line</span>
              : <span className="text-slate-500">Full approved amount</span>}
          </div>
        </div>
        <PaymentFields f={f} setF={setF} banks={ctx?.banks || []} selfPay={selfPay} testId="disburse" />
        <div><Label>Note</Label><Textarea rows={2} value={f.note} onChange={e => setF({ ...f, note: e.target.value })} data-testid="disburse-note" /></div>
        <p className="text-xs text-slate-500">No TDS on advances – TDS is applied at settlement on the bills. The 30-day settlement clock starts from the payment date.</p>
        <details className="rounded border p-2" open data-testid="disburse-details"><summary className="text-sm cursor-pointer">Request details & approval history</summary><div className="mt-2"><RequestDetailsLoader kind="advance" id={adv.id} /></div></details>
        {block && <p className="text-xs text-rose-700" data-testid="disburse-block">{block}</p>}
        <DialogFooter><Button disabled={busy || !!block} onClick={submit} data-testid="save-disburse-btn">{busy ? "Saving…" : `Disburse ${inr(amt)}`}</Button></DialogFooter>
      </div>
    </>
  );
}

export function DisbursementCorrectForm({ adv, onSaved }) {
  const banks = useRkfBanks();
  const [f, setF] = useState({ pay_date: (adv.pay_date || adv.disbursed_at || "").slice(0, 10), mode: adv.pay_mode || "NEFT", utr: adv.utr || "",
    paid_from_id: adv.paid_from_id || "", proof: adv.payment_proof_file_id ? [{ id: adv.payment_proof_file_id, original_filename: "Current proof" }] : [], note: "" });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const block = paymentMissing(f, adv.self_disbursed) || (!reason.trim() ? "A reason is required" : null);
  const submit = async () => {
    setBusy(true);
    try {
      await api.put(`/advances/${adv.id}/disbursement`, { pay_date: f.pay_date, mode: f.mode, utr: f.utr, paid_from_id: f.paid_from_id, proof_file_id: f.proof[0]?.id, reason });
      toast.success("Disbursement corrected");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>Correct disbursement · {adv.advance_id}</DialogTitle></DialogHeader>
      <div className="space-y-3 mt-2" data-testid="disb-correct-form">
        <p className="text-xs text-slate-500">Accounts can correct within 7 days of disbursement and before settlement starts; after that only the Super Admin. Every change is logged.</p>
        <PaymentFields f={f} setF={setF} banks={banks} selfPay={adv.self_disbursed} testId="disb-correct" />
        <div><Label>Reason for correction *</Label><Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} data-testid="disb-correct-reason" /></div>
        {(adv.disbursement_changes || []).length > 0 && (
          <div className="rounded border p-2 text-xs" data-testid="disb-change-log">
            <div className="font-semibold mb-1">Change log</div>
            {adv.disbursement_changes.map((c, i) => <div key={i}>{(c.at || "").slice(0, 10)} · {c.by_name}: {c.changes.map(x => `${x.field} ${x.old} → ${x.new}`).join("; ")} — {c.reason}</div>)}
          </div>
        )}
        {block && <p className="text-xs text-rose-700">{block}</p>}
        <DialogFooter><Button disabled={busy || !!block} onClick={submit} data-testid="disb-correct-save">{busy ? "Saving…" : "Save correction"}</Button></DialogFooter>
      </div>
    </>
  );
}
